import React, { useMemo, useCallback, useContext } from 'react';
import { Dropdown, Button, Space } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import type { DropdownProps as AntDropdownProps, MenuProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { createCommand, parseAction } from '../../base/actionParser';
import { DataSetInheritContext } from '../../base/DataSetInheritContext';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

// 菜单项类型
interface MenuItem {
  key: string;
  label: string;
  icon?: string;
  disabled?: boolean;
  danger?: boolean;
  divider?: boolean;
  children?: MenuItem[];
}

/** 与 Ant Design Dropdown/Menu 事件对应的 command 动作名，用于 Extend 中 createWatchCommands 注册 */
export interface AndDropdownActionProps {
  /** 点击菜单项（Menu onClick） */
  clickAction?: string;
  /** 菜单项被选中（Menu onSelect，selectable 时） */
  selectAction?: string;
  /** 菜单项取消选中（Menu onDeselect，multiple 时） */
  deselectAction?: string;
  /** 下拉框显示/隐藏（Dropdown onOpenChange） */
  openChangeAction?: string;
}

export interface AndDropdownProps
  extends Omit<AntDropdownProps, 'menu' | 'children' | 'onOpenChange'>,
    BaseComponentProps,
    DataSetConfig,
    AndDropdownActionProps {
  /** 按钮文本 */
  text?: string;
  /** 菜单项配置 */
  items?: MenuItem[] | string;
  /** 子元素 */
  children?: React.ReactNode;
  /** (Internal) 是否有 GrapesJS 子组件 */
  hasChildren?: boolean;
  /** 直接透传：下拉框显示状态变化 */
  onOpenChange?: (open: boolean, info: { source: 'trigger' | 'menu' }) => void;
  /** 迭代器内当前行序号，供 clickAction/selectAction 等使用 @index 占位符（与 AndSelect 一致） */
  index?: number;
}

// 解析菜单项
const parseItems = (items: MenuItem[] | string | undefined): MenuItem[] => {
  if (!items) return [];
  if (typeof items === 'string') {
    try {
      return JSON.parse(items);
    } catch {
      return [];
    }
  }
  return items;
};

// 转换为 Ant Design Menu 格式
const convertToMenuItems = (items: MenuItem[]): MenuProps['items'] => {
  return items.map(item => {
    if (item.divider) {
      return { type: 'divider' as const, key: item.key };
    }
    return {
      key: item.key,
      label: item.label,
      disabled: item.disabled,
      danger: item.danger,
      children: item.children ? convertToMenuItems(item.children) : undefined,
    };
  });
};

// ... (omit parseItems and convertToMenuItems)

export const AndDropdown: React.FC<AndDropdownProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  text = '下拉菜单',
  items: propItems,
  clickAction,
  selectAction,
  deselectAction,
  openChangeAction,
  onOpenChange: propOnOpenChange,
  children,
  hasChildren,
  index,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const ctx = useContext(DataSetInheritContext);
  const effectiveIndex = index ?? ctx?.index;
  const effectiveParentIndex = ctx?.parentIndex;
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  // 订阅 store，使 setDataSet 更新时组件能重渲染（仅读不订阅则不会响应）
  const storeValue = useAndStore((state) => {
    if (!dataSetKey) return undefined;
    if (dataSetField) return state.getDataSet(dataSetKey, dataSetField);
    return state.dataSets[dataSetKey];
  });

  // 从 store 派生菜单项，兼容多种约定（与 AndMenu 一致）：
  // 1) dataSetField 指向数组时，storeValue 直接为 MenuItem[]
  // 2) 只配 dataSet 未配 dataSetField 时，Extend 常用 setDataSet(key, '', list) → storeValue 为整块（数组或 { value: MenuItem[] }）
  // 3) 或存 { items: MenuItem[] }
  const storeItems = useMemo((): MenuItem[] | undefined => {
    if (storeValue === undefined || storeValue === null) return undefined;
    if (Array.isArray(storeValue)) return storeValue as MenuItem[];
    const obj = storeValue as Record<string, unknown>;
    if (Array.isArray(obj?.items)) return obj.items as MenuItem[];
    if (Array.isArray(obj?.value)) return obj.value as MenuItem[];
    return undefined;
  }, [storeValue]);

  // 设计器模式(develop)：优先 store，无则回退到静态 items 便于画布预览；运行时且配置了 dataSet：仅用 store，不回退到静态 items
  const items = useMemo(() => {
    if (develop) return storeItems ?? parseItems(propItems) ?? [];
    if (dataSetKey) return storeItems ?? [];
    return parseItems(propItems) ?? [];
  }, [develop, dataSetKey, storeItems, propItems]);

  // Dropdown 显示/隐藏（官方：onOpenChange(open, { source: 'trigger' | 'menu' })）
  const handleOpenChange = useCallback(
    (open: boolean, info: { source: 'trigger' | 'menu' }) => {
      if (propOnOpenChange) {
        propOnOpenChange(open, info);
        return;
      }
      if (openChangeAction && sessionId) {
        const { actionName, arg } = parseAction(
          openChangeAction,
          open,
          {
            id,
            dataSet,
            dataSetField,
            index: effectiveIndex,
            parentIndex: effectiveParentIndex,
            source: info.source,
          },
          ctx ?? undefined
        );
        addCommand(sessionId, createCommand(actionName, arg));
      }
    },
    [
      propOnOpenChange,
      openChangeAction,
      sessionId,
      addCommand,
      id,
      dataSet,
      dataSetField,
      effectiveIndex,
      effectiveParentIndex,
      ctx,
    ]
  );

  // 转换为菜单配置（Menu onClick / onSelect / onDeselect 对应官方 API；info 类型由 MenuProps 推断，domEvent 为 MouseEvent | KeyboardEvent）
  const menuProps = useMemo<MenuProps>(
    () => ({
      items: convertToMenuItems(items),
      onClick: (info) => {
        if (clickAction && sessionId) {
          const { actionName, arg } = parseAction(
            clickAction,
            info.key,
            {
              id,
              dataSet,
              dataSetField,
              index: effectiveIndex,
              parentIndex: effectiveParentIndex,
              key: info.key,
              keyPath: info.keyPath,
            },
            ctx ?? undefined
          );
          addCommand(sessionId, createCommand(actionName, arg));
        }
      },
      onSelect: (info) => {
        if (selectAction && sessionId) {
          const { actionName, arg } = parseAction(
            selectAction,
            info.key,
            {
              id,
              dataSet,
              dataSetField,
              index: effectiveIndex,
              parentIndex: effectiveParentIndex,
              key: info.key,
              keyPath: info.keyPath,
              selectedKeys: info.selectedKeys,
            },
            ctx ?? undefined
          );
          addCommand(sessionId, createCommand(actionName, arg));
        }
      },
      onDeselect: (info) => {
        if (deselectAction && sessionId) {
          const { actionName, arg } = parseAction(
            deselectAction,
            info.key,
            {
              id,
              dataSet,
              dataSetField,
              index: effectiveIndex,
              parentIndex: effectiveParentIndex,
              key: info.key,
              keyPath: info.keyPath,
              selectedKeys: info.selectedKeys,
            },
            ctx ?? undefined
          );
          addCommand(sessionId, createCommand(actionName, arg));
        }
      },
    }),
    [
      items,
      clickAction,
      selectAction,
      deselectAction,
      sessionId,
      addCommand,
      id,
      dataSet,
      dataSetField,
      effectiveIndex,
      effectiveParentIndex,
      ctx,
    ]
  );
  // 在 GrapesJS 中，我们将把子组件（如 Button）移动到这个 Slot 中
  const triggerElement = useMemo(() => {
    return (
      <div 
        className="and-dropdown-trigger-slot" 
        style={{ display: 'inline-block', cursor: 'pointer' }}
      >
        {/* 如果有 React children (非设计器模式下可能用到) 则渲染 */}
        {children}
        
        {/* 设计器模式下，且没有 React children，且 GrapesJS 没有子组件时显示占位符 */}
        {develop && !children && !hasChildren && (
            <div style={{ padding: '4px 8px', border: '1px dashed #d9d9d9', color: '#ccc', fontSize: 12 }}>
               请配置触发器
            </div>
        )}

        {/* 
           运行模式下，如果没有 children，使用 text 渲染默认按钮 (作为后备)
        */}
        {!children && !develop && text && (
           <Button>
             <Space>
               {text}
               <DownOutlined />
             </Space>
           </Button>
        )}
      </div>
    );
  }, [children, develop, text, hasChildren]);

  return (
    <Dropdown {...restProps} menu={menuProps} onOpenChange={handleOpenChange}>
      {triggerElement}
    </Dropdown>
  );
};

export default AndDropdown;
